import Typography from '@components/basics/typography';
import React, { FunctionComponent } from 'react';

import styled, { useTheme } from 'styled-components';

import AchievementCircleIcon from '../dashboard/achievements/achievement-circle-icon';

interface CounterProps {
  className?: string;
  current: number;
  max: number;
  monochrome?: boolean;
  customSize?: number;
  customMax?: number;
  disabled?: boolean;
}

const Counter: FunctionComponent<CounterProps> = ({
  className,
  current,
  max,
  monochrome,
  customSize,
  customMax,
  disabled,
}) => {
  const theme = useTheme();

  const getTextColor = (current: number, max: number) => {
    if (current === max && monochrome) {
      if (disabled) {
        return theme.colors.black;
      }
      return theme.colors.white;
    }
    if (monochrome) {
      return theme.colors.white;
    }
    if (current === max) {
      return theme.colors.blueColor;
    }
    return theme.colors.primary;
  };

  return (
    <div className={className || ''}>
      <Content>
        <CurrentText
          type="subheader"
          customFont="medium"
          className={`${monochrome ? 'monochrome' : ''} ${
            current === max && monochrome ? 'completed' : ''
          } ${disabled ? 'disabled' : ''}`}
          color={getTextColor(current, max)}
        >
          {current}{' '}
        </CurrentText>
        <MaxText
          className={`${monochrome ? 'monochrome' : ''} ${
            current === max && monochrome ? 'completed' : ''
          } ${disabled ? 'disabled' : ''}`}
          customFont="medium"
          color={getTextColor(current, max)}
        >
          <span className={current !== max || monochrome ? 'gray' : ''}>/</span>
          <span className={monochrome ? 'gray' : ''}>{max}</span>
        </MaxText>
      </Content>
      <AchievementCircleIcon
        className="circle"
        max={max}
        current={current}
        monochrome={monochrome}
        customSize={customSize}
        customMax={customMax}
        disabled={disabled}
      />
    </div>
  );
};

export default styled(Counter)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.customSize ? `${props.customSize}px` : '47px')};
  height: ${(props) => (props.customSize ? `${props.customSize}px` : '47px')};

  .circle {
    position: absolute;
    inset: 0;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
`;

const CurrentText = styled(Typography)`
  line-height: ${({ theme }) => theme.typography.h2};
  &.monochrome {
    &.completed {
      color: ${({ theme }) => theme.colors.white};
    }
    &.disabled {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

const MaxText = styled(Typography)`
  font-size: 8px !important;
  line-height: ${({ theme }) => theme.typography.h2};
  .gray {
    font-size: 8px !important;
    line-height: ${({ theme }) => theme.typography.h2};
    color: ${({ theme }) => theme.colors.tertiary};
  }
  &.monochrome {
    .gray {
      opacity: 0.5;
      color: ${({ theme }) => theme.colors.white};
    }
    &.completed {
      .gray {
        opacity: 1;
        color: ${({ theme }) => theme.colors.white};
      }
    }
    &.disabled {
      .gray {
        opacity: 1;
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
`;
